import React, { useState, useContext, useCallback, useEffect } from 'react';
import { Box, CssBaseline } from '@mui/material';
import AppHeader from './AppHeader';
import Sidebar from './Sidebar';
import { UserContext } from '../Blocks/UserProvider';

// Create a context to hold the header customization functions
const DashboardContext = React.createContext({
    setHeaderContent: () => {},
    setHeaderActions: () => {}
});

const Dashboard = ({ children, dashboardTitle, breadcrumbs, customHeaderActions }) => {
    const [open, setOpen] = useState(true);
    const { user } = useContext(UserContext);
    
    // States to store header customizations
    const [customHeader, setCustomHeader] = useState(null);
    const [headerActions, setHeaderActions] = useState(null);

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    // Safe setter for custom header content
    const setHeaderContentSafe = useCallback((content) => {
        setCustomHeader(content);
    }, []);

    // Safe setter for header actions
    const setHeaderActionsSafe = useCallback((actions) => {
        setHeaderActions(actions);
    }, []);

    // Clear customizations when dashboard unmounts or title changes
    useEffect(() => {
        return () => {
            setCustomHeader(null);
            setHeaderActions(null);
        };
    }, [dashboardTitle]);

    // Create context value with header control functions
    const dashboardContextValue = React.useMemo(() => ({
        setHeaderContent: setHeaderContentSafe,
        setHeaderActions: setHeaderActionsSafe
    }), [setHeaderContentSafe, setHeaderActionsSafe]);

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <CssBaseline />
            
            {/* App Header/Top Bar */}
            <AppHeader 
                open={open} 
                handleDrawerOpen={handleDrawerOpen} 
                title={dashboardTitle} 
                customHeader={customHeader}
                breadcrumbs={breadcrumbs}
                actions={customHeaderActions || headerActions}
                user={user?.email}
            />
            
            {/* Sidebar Navigation */}
            <Sidebar 
                open={open} 
                handleDrawerClose={handleDrawerClose} 
            />
            
            {/* Main Content */}
            <Box component="main" sx={{ 
                flexGrow: 1, 
                p: 3,
                pt: 7.5,
                backgroundColor: '#f8fafc',
                backgroundImage: "url('/dist/img/baseapp_background.svg')",
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                overflow: 'auto'
            }}>
                {/* Provide context to children instead of cloning with props */}
                <DashboardContext.Provider value={dashboardContextValue}>
                    {children}
                </DashboardContext.Provider>
            </Box>
        </Box>
    );
};

// Export the context for use in child components
export const useDashboard = () => useContext(DashboardContext);

export default Dashboard;
